import './styles/BulkImagePreviewer.scss';
import { AppContext } from '../Context';
import { Fragment, useContext, useState, useRef, useEffect, createRef } from 'react';
import ZoomImg from './ZoomImg';
import { Filesystem, Directory, PermissionStatus } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { isPlatform } from '@ionic/react';
import ReactDOM from 'react-dom/client';
import { Mediastore } from '@agorapulse/capacitor-mediastore';

//controllers
import DatabaseInterfaceController from '../controllers/DatabaseInterfaceController';

//mui icons
import CloseIcon from '@mui/icons-material/Close';
import { request } from 'http';
import { System_DeviceOrientationMap } from '../assets/keyMap';

class Position { x: number = 0; y: number = 0; };

const nonstate = {
    imageTouchInterval: 500,
    imageSwipeTreshold: 100,
    defaultSlideSpeed: 250
}

interface Props {
    src: string, //current selected image
    index: number, //current selected image index
    srcs: string[], //all other images
    style?: any,
    fillContainer?: boolean,
    slideSpeed?: number, //milliseconds
    shareable?: boolean,
    downloadable?: boolean
}

const BulkImagePreviewer: React.FC<Props> = ({ src, index, srcs, style, fillContainer, slideSpeed, shareable, downloadable }) => {
    //contexts
    const appContext = useContext(AppContext);
    //states
    const [shouldShowImgPreview, SetShouldShowImgPreview] = useState<boolean>(false);
    const [currentAppListenerActions, SetCurrentAppListenerActions] = useState<any[] | null>(null);
    const [currentSelectedIndex, SetCurrentSelectedIndex] = useState<number>(-1);
    const [slideDirection, SetSlideDirection] = useState<string>(''); //left, right, ''
    //refs
    const downloadShareModeRef = useRef<string>(''); //download or share
    const imagePreviewRef = useRef<any>(null);
    const currentSelectedIndexRef = useRef(currentSelectedIndex);
    const slideSpeedRef = useRef<number>(slideSpeed != undefined ? slideSpeed : nonstate.defaultSlideSpeed);
    const scrollImgRefs = useRef<any[]>([] as any[]);
    //refs - touch handling
    const imageTouchIntervalRef = useRef<any>(null);
    const imageTouchStartRef = useRef<boolean>(false);
    const touchStartPosRef = useRef<Position>({ x: 0, y: 0 } as Position);
    //refs - objs
    const leftImgRef = useRef<any>(null);
    const midImgRef = useRef<any>(null);
    const rightImgRef = useRef<any>(null);
    const zoomImgRef = useRef<any>(null);
    //controllers
    const databaseInterfaceController = new DatabaseInterfaceController(appContext);

    //watchers
    //test
    // useEffect(() => {
    //     console.log('BulkImagePreviewerTest: ' + currentSelectedIndex);
    // }, [currentSelectedIndex]);

    //Initialize
    useEffect(() => {
        return (() => {
            if (imageTouchIntervalRef.current != null)
                clearInterval(imageTouchIntervalRef.current);
        });
    }, []);

    //watch for app listener update currentAppListenerActions
    useEffect(() => {
        if (!shouldShowImgPreview) {
            //only when imagePreview screen is off
            SetCurrentAppListenerActions([...appContext.appListenerActions]);
        }
    }, [appContext.appListenerActions]);

    //watch for database result actions
    useEffect(() => {
        if (shouldShowImgPreview && appContext.databaseActionModeQueue.length > 0 && appContext.databaseActionParamsQueue.length > 0 && appContext.databaseResultAction != null) {
            const ExecuteDatabaseResultAction = async () => {
                const [res, mode] = await appContext.databaseResultAction();

                switch (mode) {
                    case 'fetchImage':
                        if (!JSON.stringify(res).includes('_false')) {
                            switch (downloadShareModeRef.current) {
                                case 'download':
                                    DownloadImage(res);
                                    break;
                                case 'share':
                                    ShareImage(res);
                                    break;
                            }
                        }
                        break;
                }
            };

            switch (appContext.databaseActionModeQueue[0]) {
                case 'fetchImage':
                    ExecuteDatabaseResultAction();
                    break;
            }
        }
    }, [appContext.databaseActionModeQueue, appContext.databaseResultAction]);

    //watch for should show image preview, attach imagePreview to document, else remove imagePreview from document
    useEffect(() => {
        const appContainer = document.getElementsByClassName('App');
        if (shouldShowImgPreview) {
            if (appContainer.length > 0 && imagePreviewRef.current != null) {
                appContainer[0].removeChild(imagePreviewRef.current);
                imagePreviewRef.current = null;
            }

            imagePreviewRef.current = document.createElement('div');
            const root = ReactDOM.createRoot(imagePreviewRef.current);
            root.render(RenderImagePreview());

            if (appContainer.length > 0) {
                appContainer[0].appendChild(imagePreviewRef.current)
            }

            //override backbutton listener to close preview
            appContext.SetAppListenerActions([
                ['backButton', () => { SetShouldShowImgPreview(false); }],
                ['appStateChange', GetAppListenerAction('appStateChange')],
                ['appUrlOpen', GetAppListenerAction('appUrlOpen')]
            ]);
            //current version of app controller, need to add in all the listeners to counter 
        } else {
            if (imagePreviewRef.current != null && appContainer.length > 0) {
                appContainer[0].removeChild(imagePreviewRef.current);
                imagePreviewRef.current = null;
            }

            //remove backbutton listener, revert back to original behavior
            //add backbutton listener to close preview
            if (currentAppListenerActions != null)
                appContext.SetAppListenerActions([...currentAppListenerActions]);
        }
    }, [shouldShowImgPreview, slideDirection]);

    //watch for src & srcs updated, initialize currentSelectedIndex
    useEffect(() => {
        if (shouldShowImgPreview && src != '' && srcs.length > 0 && currentSelectedIndex == -1) {
            srcs.map((_src: string, i: number) => {
                if (_src == src)
                    SetCurrentSelectedIndex(i);
            });
        }
    }, [shouldShowImgPreview, src, srcs]);

    //watch for shouldShowImgPreview, reset transitionEffects
    useEffect(() => {
        if (shouldShowImgPreview) {
            setTimeout(() => {
                //initialize, add transition effects to 3 images
                SetupTransitionEffects('reset');
                scrollImgRefs.current[currentSelectedIndexRef.current].scrollIntoView({ behavior: 'smooth', inline: 'center' });
            }, 100);
        }
    }, [shouldShowImgPreview]);

    //watch for currentSelectedIndex update mainImgSrc
    useEffect(() => {
        if (
            leftImgRef.current != null
            && midImgRef.current != null
            && rightImgRef.current != null
        ) {
            leftImgRef.current.src = srcs[currentSelectedIndex - 1 > 0 ? currentSelectedIndex - 1 : srcs.length - 1];
            zoomImgRef.current.src = srcs[currentSelectedIndex];
            rightImgRef.current.src = srcs[currentSelectedIndex + 1 < srcs.length ? currentSelectedIndex + 1 : 0];
        }
    }, [currentSelectedIndex]);

    //ui functions
    const OnImageSelected = () => {
        currentSelectedIndexRef.current = index;
        SetCurrentSelectedIndex(currentSelectedIndexRef.current);
        SetShouldShowImgPreview(true);
    }
    const OnCloseBtnClicked = () => {
        SetShouldShowImgPreview(false);
    }
    const OnImgTouchStart = (e: any) => {
        if (imageTouchIntervalRef.current != null)
            clearInterval(imageTouchIntervalRef.current);

        imageTouchIntervalRef.current = setInterval(() => {
            //used hold for nonstate.imageTouchInterval
            clearInterval(imageTouchIntervalRef.current);

            if(
                (downloadable == undefined  || downloadable)
                && (shareable == undefined || shareable)
            ) {
                appContext.SetPopupMessage('Would you like to download or share\n the image ?');
                appContext.SetPopupButtons(['Download', 'Share', 'Cancel']);
                appContext.SetPopupActions([() => OnDownloadBtnClicked(), () => OnShareImgClicked(), () => { }]);
            } else if(
                downloadable == undefined  || downloadable
            ) {
                appContext.SetPopupMessage('Would you like to download the image ?');
                appContext.SetPopupButtons(['Download', 'Cancel']);
                appContext.SetPopupActions([() => OnDownloadBtnClicked(), () => { }]);
            } else if(
                shareable == undefined || shareable
            ) {
                appContext.SetPopupMessage('Would you like to share the image ?');
                appContext.SetPopupButtons(['Share', 'Cancel']);
                appContext.SetPopupActions([() => OnShareImgClicked(), () => { }]);
            }

        }, nonstate.imageTouchInterval);

        if (e.touches.length == 1 && !imageTouchStartRef.current) {
            imageTouchStartRef.current = true;
            touchStartPosRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY } as Position;
        }
    }
    const OnImgTouchMove = (e: any) => {
        //user attempt to hold and zoom img
        if (imageTouchIntervalRef.current != null)
            clearInterval(imageTouchIntervalRef.current);

        if (e.touches.length == 1 && imageTouchStartRef.current) {
            const xMoved = e.touches[0].clientX - touchStartPosRef.current.x;
            if (Math.abs(xMoved) >= nonstate.imageSwipeTreshold) {
                // SetSlideDirection(xMoved > 0 ? 'slide-right' : 'slide-left'); ---deprecated
                let swipeDir = ''; //left - right
                if (xMoved > 0) {
                    //slide right
                    requestAnimationFrame(() => {
                        leftImgRef.current.style.transform = `translate(0%)`;
                        midImgRef.current.style.transform = `translate(100%)`;
                        rightImgRef.current.style.transform = `translate(200%)`;

                        leftImgRef.current.style.zIndex = '1';
                        rightImgRef.current.style.zIndex = '-1';
                    });
                    swipeDir = 'right';
                } else {
                    //slide left
                    requestAnimationFrame(() => {
                        leftImgRef.current.style.transform = `translate(-200%)`;
                        midImgRef.current.style.transform = `translate(-100%)`;
                        rightImgRef.current.style.transform = `translate(0%)`;

                        rightImgRef.current.style.zIndex = '1';
                        leftImgRef.current.style.zIndex = '-1';
                    });
                    swipeDir = 'left';
                }

                //reset image positions to ready for next cycle
                setTimeout(() => {
                    //get next currentSelectedIndex
                    if (swipeDir == 'left') {
                        if (currentSelectedIndexRef.current + 1 < srcs.length)
                            currentSelectedIndexRef.current += 1;
                        else
                            currentSelectedIndexRef.current = 0;
                    } else if (swipeDir == 'right') {
                        if (currentSelectedIndexRef.current - 1 >= 0)
                            currentSelectedIndexRef.current -= 1;
                        else
                            currentSelectedIndexRef.current = srcs.length - 1;
                    }
                    SetCurrentSelectedIndex(currentSelectedIndexRef.current);

                    //disable all transition  effects
                    SetupTransitionEffects('remove');

                    //reset middle image
                    midImgRef.current.style.transform = `translate(0%)`;
                    zoomImgRef.current.src = srcs[currentSelectedIndexRef.current];

                    //delay and reset left & right image
                    zoomImgRef.current.onload = () => {
                        //reset left & right image positions
                        leftImgRef.current.style.transform = `translate(-100%)`;
                        rightImgRef.current.style.transform = `translate(100%)`;

                        //add back transition animations
                        setTimeout(() => {
                            //update left & right images
                            leftImgRef.current.src = srcs[currentSelectedIndexRef.current - 1 >= 0 ? currentSelectedIndexRef.current - 1 : srcs.length - 1];
                            rightImgRef.current.src = srcs[currentSelectedIndexRef.current + 1 < srcs.length ? currentSelectedIndexRef.current + 1 : 0];

                            //add back transition effects
                            SetupTransitionEffects('reset');
                        }, 100);
                    }

                    //scroll selected scrollImg intoView
                    ResetScrollView();

                }, slideSpeedRef.current * 1.5);

                //reset imgTouchhStartRef
                imageTouchStartRef.current = false;
            }
        }
    }
    const OnImgTouchEnd = () => {
        if (imageTouchIntervalRef.current != null)
            clearInterval(imageTouchIntervalRef.current);

        imageTouchStartRef.current = false;
    }
    const OnDownloadBtnClicked = () => {
        appContext.SetWaitScreenMessage('Preparing image please wait');
        appContext.SetWaitScreenShouldShow(true);
        downloadShareModeRef.current = 'download';

        //fetch imageBase64String from server
        databaseInterfaceController.Queue('fetchImage', [
            ['fetchImage', 'true'],
            ['imageUrl', srcs[currentSelectedIndexRef.current]]
        ]);
    }
    const OnShareImgClicked = async () => {
        appContext.SetWaitScreenMessage('Preparing image please wait');
        appContext.SetWaitScreenShouldShow(true);
        downloadShareModeRef.current = 'share';

        //fetch imageBase64String from server
        databaseInterfaceController.Queue('fetchImage', [
            ['fetchImage', 'true'],
            ['imageUrl', srcs[currentSelectedIndexRef.current]]
        ]);
    }
    const OnScrollImageSelect = (i: number) => {
        currentSelectedIndexRef.current = i;
        SetCurrentSelectedIndex(currentSelectedIndexRef.current);
        ResetScrollView();
    }

    //render funtions
    const RenderImagePreview = () => {
        //slide direction update state not working, try assign element to ref, then modify the className
        return (
            <div className={'BulkImagePreviewer-image-preview ' + (appContext.deviceOrientation == System_DeviceOrientationMap.LANDSCAPE ? 'landscape' : 'portrait')}>
                <button className='BulkImagePreviewer-image-preview-background-mask' onClick={() => SetShouldShowImgPreview(false)}></button>
                <div className='BulkImagePreviewer-image-preview-main'>
                    {/** Left Image */}
                    <img ref={leftImgRef} className={'BulkImagePreviewer-image-preview-img left ' + slideDirection} src={currentSelectedIndex > 0 ? srcs[currentSelectedIndex - 1] : ''} />
                    {/** Mid Image */}
                    <div ref={midImgRef} className={'BulkImagePreviewer-image-preview-img'} onTouchStart={OnImgTouchStart} onTouchEnd={() => OnImgTouchEnd()} onTouchMove={OnImgTouchMove}>
                        <ZoomImg imgRef={zoomImgRef} src={srcs[currentSelectedIndex]} />
                    </div>
                    {/** Right Image */}
                    <img ref={rightImgRef} className={'BulkImagePreviewer-image-preview-img right ' + slideDirection} src={((currentSelectedIndex + 1) < srcs.length) ? srcs[currentSelectedIndex + 1] : ''} />
                </div>
                <div className='BulkImagePreviewer-image-preview-scroll-view'>
                    {/** small images scroll view here */}
                    {
                        srcs.map((_src: string, i: number) => {
                            return (
                                <button ref={(el) => (scrollImgRefs.current[i] = el)} key={'BulkImagePreviewer-image-preview-scroll-btn-' + i} className={'BulkImagePreviewer-image-preview-scroll-btn ' + (i == currentSelectedIndexRef.current ? 'selected' : '')} onClick={() => OnScrollImageSelect(i)}>
                                    <img src={_src} />
                                </button>
                            )
                        })
                    }
                </div>
                <button className='BulkImagePreviewer-image-preview-close-btn' onClick={() => OnCloseBtnClicked()}><CloseIcon /></button>
            </div>
        )
    }

    //exe functions
    const SaveFileToLocal = async (directory: Directory, base64String: string, fileName: string) => {
        await Filesystem.writeFile({
            path: fileName,
            data: base64String, // your data to write (ex. base64)
            directory: /*Directory.Documents ---not working android 10 onwards*/Directory.Cache,
            recursive: true
        });
        const uri = await Filesystem.getUri({
            directory: directory,
            path: fileName
        });

        if (uri)
            return uri.uri;
        else
            return '';
    }
    const DownloadImage = async (base64String: string) => {
        const pathArr = src.split('/');
        const fileName = pathArr[pathArr.length - 1];

        if (isPlatform('hybrid')) {
            const uri = await SaveFileToLocal(Directory.Cache, base64String, fileName);
            const SaveFile = async () => {
                // const filePath = 'PPKM_Event_Gallery/' + fileName;
                // await Filesystem.writeFile({
                //     path: filePath,
                //     data: base64String, // your data to write (ex. base64)
                //     directory: Directory.Documents,
                //     recursive: true
                // });
                // appContext.SetPopupMessage('Downloaded Successfully!\nSaved in Documents/PPKM_Event_Gallery/');
                try {
                    await Mediastore.saveToDownloads({
                        path: uri,
                        filename: fileName
                    }).then((res: any) => {
                        console.log('saveToDownloadRes: ' + JSON.stringify(res));
                    }).catch((err: string) => {
                        console.log('saveToDownloadsErr: ' + err);
                    });
                    appContext.SetPopupMessage('Successfully saved to downloads folder.');
                } catch (err: any) {
                    console.log('getUriErr: ' + JSON.stringify(err));
                    appContext.SetPopupMessage('Fail to download picture.');
                }
            }
            await SaveFile();
        } else {
            var a = document.createElement("a"); //Create <a>
            a.href = "data:image/png;base64," + base64String; //Image Base64 Goes here
            a.download = fileName; //File name Here
            a.click(); //Downloaded file
            console.log('shouldDownloadImage');
        }
        appContext.SetWaitScreenShouldShow(false);
    }
    const ShareImage = async (base64String: string) => {
        if (isPlatform('hybrid')) {
            //save base64String to file *must save in local first before sharable
            const pathArr = src.split('/');
            const fileName = pathArr[pathArr.length - 1];
            try {
                appContext.SetWaitScreenMessage('Sharing Image Please Wait');
                appContext.SetWaitScreenShouldShow(true);
                const uri: string = await SaveFileToLocal(Directory.Cache, base64String, fileName);
                appContext.SetWaitScreenShouldShow(false);
                appContext.SetAppStateChangeWatch(false);
                Share.share({
                    text: appContext.user.name + ' is sharing an image via PPKM Super App',
                    files: [uri]
                }).then((res) => {
                    appContext.SetPopupMessage("Image successfully shared.");
                    appContext.SetAppStateChangeWatch(true);
                }).catch((err) => {
                    appContext.SetPopupMessage("Sharing failed!");
                    appContext.SetAppStateChangeWatch(true);
                });
            } catch (err) {
                console.log('some error while write file: ' + err);
            }
        } else {
            console.log('FileSharingNotSurportedInWeb');
        }
        appContext.SetWaitScreenShouldShow(false);
    }
    const GetAppListenerAction = (mode: string) => {
        let res = null;
        appContext.appListenerActions.map((actionArr: any[]) => {
            if (actionArr[0] == mode)
                res = actionArr[1];
        })

        return res;
    }
    const SetupTransitionEffects = (mode: string) => {
        if (
            leftImgRef.current != null
            && midImgRef.current != null
            && rightImgRef.current != null
        ) {
            if (mode == 'reset') {
                leftImgRef.current.style.transition = `transform ${slideSpeedRef.current / 1000}s`;
                midImgRef.current.style.transition = `transform ${slideSpeedRef.current / 1000}s`;
                rightImgRef.current.style.transition = `transform ${slideSpeedRef.current / 1000}s`;
            } else if (mode == 'remove') {
                leftImgRef.current.style.transition = 'none';
                midImgRef.current.style.transition = 'none';
                rightImgRef.current.style.transition = 'none';
            }
        }
    }
    const ResetScrollView = () => {
        scrollImgRefs.current[currentSelectedIndexRef.current].scrollIntoView({ behavior: 'smooth', inline: 'center' });
        scrollImgRefs.current.map((imgRef: any) => {
            if(imgRef != null) {
                imgRef.className = imgRef.className.replace('selected', '');
            }
        });
        scrollImgRefs.current[currentSelectedIndexRef.current].className += 'selected';
    }

    return (
        <Fragment>
            <button className={'BulkImagePreviewer-btn ' + (fillContainer ? 'fill' : '')} style={style != undefined ? style : {}} onClick={() => OnImageSelected()}><img className='BulkImagePreviewer-btn-img' src={src} /></button>
        </Fragment>
    )
}
export default BulkImagePreviewer;